"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { SortingState } from '@tanstack/react-table'
import type { FilterValues } from '@open-mercato/ui/backend/FilterBar'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { Page, PageBody, PageHeader } from '@open-mercato/ui/backend/Page'
import { DynamicTable } from '@open-mercato/ui/backend/DynamicTable'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { buildCrudQuery, deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { Button } from '@open-mercato/ui/primitives/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@open-mercato/ui/primitives/tabs'
import {
  buildLegsColumns,
  buildLegsFilters,
  type LegListItem,
  type LegsListResponse,
} from '../lib/legTable'
import {
  buildDispatchesColumns,
  buildDispatchesFilters,
  type DispatchListItem,
  type DispatchesListResponse,
} from '../lib/dispatchTable'
import { AssignmentDialog } from '../lib/assignmentDialog'
import { LegFormDialog } from '../lib/legFormDialog'
import { DispatchEditDialog } from '../lib/dispatchEditDialog'

const DEFAULT_PAGE_SIZE = 25

function buildLegsListParams(page: number, search: string, filterValues: FilterValues, sorting: SortingState) {
  const sort = sorting[0]
  return {
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    q: search.trim() || undefined,
    status: typeof filterValues.status === 'string' ? filterValues.status : undefined,
    vehicleType: typeof filterValues.vehicleType === 'string' ? filterValues.vehicleType : undefined,
    sortField: sort?.id,
    sortDir: sort?.desc ? 'desc' : 'asc',
  }
}

function buildDispatchesListParams(page: number, filterValues: FilterValues, sorting: SortingState) {
  const sort = sorting[0]
  return {
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    status: typeof filterValues.status === 'string' ? filterValues.status : undefined,
    sortField: sort?.id,
    sortDir: sort?.desc ? 'desc' : 'asc',
  }
}

async function fetchLegsList(params: ReturnType<typeof buildLegsListParams>): Promise<LegsListResponse> {
  return readApiResultOrThrow<LegsListResponse>(`/api/road/legs?${buildCrudQuery(params)}`, undefined, {
    errorMessage: 'Failed to fetch legs.',
  })
}

async function fetchDispatchesList(params: ReturnType<typeof buildDispatchesListParams>): Promise<DispatchesListResponse> {
  return readApiResultOrThrow<DispatchesListResponse>(`/api/road/dispatch/assignments?${buildCrudQuery(params)}`, undefined, {
    errorMessage: 'Failed to fetch dispatches.',
  })
}

export default function DispatchBoardPage() {
  const t = useT()
  const router = useRouter()
  const scopeVersion = useOrganizationScopeVersion()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation({ contextId: 'dispatch-board' })

  const [activeTab, setActiveTab] = React.useState('legs')

  // Legs state
  const [legsPage, setLegsPage] = React.useState(1)
  const [legsSearch, setLegsSearch] = React.useState('')
  const [legsSorting, setLegsSorting] = React.useState<SortingState>([{ id: 'scheduledPickupAt', desc: false }])
  const [legsFilterValues, setLegsFilterValues] = React.useState<FilterValues>({})

  // Leg dialogs
  const [createLegOpen, setCreateLegOpen] = React.useState(false)
  const [editLegTarget, setEditLegTarget] = React.useState<LegListItem | null>(null)
  const [editLegOpen, setEditLegOpen] = React.useState(false)
  const [assignDialogOpen, setAssignDialogOpen] = React.useState(false)
  const [assignTarget, setAssignTarget] = React.useState<LegListItem | null>(null)

  // Dispatches state
  const [dispatchesPage, setDispatchesPage] = React.useState(1)
  const [dispatchesSorting, setDispatchesSorting] = React.useState<SortingState>([{ id: 'plannedPickupAt', desc: false }])
  const [dispatchesFilterValues, setDispatchesFilterValues] = React.useState<FilterValues>({})

  // Dispatch edit dialog
  const [editDispatchTarget, setEditDispatchTarget] = React.useState<DispatchListItem | null>(null)
  const [editDispatchOpen, setEditDispatchOpen] = React.useState(false)

  const legsParams = React.useMemo(
    () => buildLegsListParams(legsPage, legsSearch, legsFilterValues, legsSorting),
    [legsPage, legsSearch, legsFilterValues, legsSorting],
  )

  const dispatchesParams = React.useMemo(
    () => buildDispatchesListParams(dispatchesPage, dispatchesFilterValues, dispatchesSorting),
    [dispatchesPage, dispatchesFilterValues, dispatchesSorting],
  )

  const legsQuery = useQuery({
    queryKey: ['dispatch', 'legs', 'list', scopeVersion, legsParams],
    queryFn: () => fetchLegsList(legsParams),
  })

  const dispatchesQuery = useQuery({
    queryKey: ['dispatch', 'dispatches', 'list', scopeVersion, dispatchesParams],
    queryFn: () => fetchDispatchesList(dispatchesParams),
    enabled: activeTab === 'dispatches',
  })

  const refreshAll = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['dispatch'] })
  }, [queryClient])

  const handleDeleteLeg = React.useCallback(async (row: LegListItem) => {
    const approved = await confirm({
      title: t('dispatch.confirm.deleteTitle', 'Delete leg?'),
      text: t('dispatch.confirm.deleteMessage', 'This will archive the leg and keep audit history intact.'),
      confirmText: t('dispatch.actions.delete', 'Delete'),
      cancelText: t('dispatch.actions.cancelEdit', 'Cancel'),
      variant: 'destructive',
    })
    if (!approved) return
    try {
      await runMutation({
        context: { kind: 'road.leg', record: { id: row.id } },
        mutationPayload: { id: row.id },
        operation: async () => {
          await deleteCrud(`road/legs/${row.id}`, {
            errorMessage: t('dispatch.messages.deleteError', 'Failed to delete leg.'),
          })
        },
      })
      flash(t('dispatch.messages.deleteSuccess', 'Leg deleted.'), 'success')
      await refreshAll()
    } catch {
      flash(t('dispatch.messages.deleteError', 'Failed to delete leg.'), 'error')
    }
  }, [confirm, refreshAll, runMutation, t])

  const handleUnassignDispatch = React.useCallback(async (row: DispatchListItem) => {
    const approved = await confirm({
      title: t('dispatch.confirm.unassignTitle', 'Unassign dispatch?'),
      text: t('dispatch.confirm.unassignMessage', 'This will release the vehicle and driver from this leg.'),
      confirmText: t('dispatch.actions.unassign', 'Unassign'),
      cancelText: t('dispatch.actions.cancelEdit', 'Cancel'),
      variant: 'destructive',
    })
    if (!approved) return
    try {
      await runMutation({
        context: { kind: 'road.dispatch', record: { id: row.id } },
        mutationPayload: { id: row.id },
        operation: async () => {
          await deleteCrud(`road/dispatch/assignments/${row.id}`, {
            errorMessage: t('dispatch.assign.messages.unassignError', 'Failed to unassign dispatch.'),
          })
        },
      })
      flash(t('dispatch.assign.messages.unassignSuccess', 'Dispatch unassigned.'), 'success')
      await refreshAll()
    } catch {
      flash(t('dispatch.assign.messages.unassignError', 'Failed to unassign dispatch.'), 'error')
    }
  }, [confirm, refreshAll, runMutation, t])

  const handleDispatchRowClick = React.useCallback((row: DispatchListItem) => {
    router.push(`/backend/dispatch/${row.id}`)
  }, [router])

  const legsColumns = React.useMemo(() => buildLegsColumns(t), [t])
  const legsFilters = React.useMemo(() => buildLegsFilters(t), [t])
  const dispatchesColumns = React.useMemo(() => buildDispatchesColumns(t), [t])
  const dispatchesFilters = React.useMemo(() => buildDispatchesFilters(t), [t])

  if (legsQuery.isLoading && !legsQuery.data) {
    return (
      <Page>
        <PageHeader
          title={t('dispatch.pages.board.title', 'Dispatch board')}
          description={t('dispatch.pages.board.description', 'Manage transport legs and assign vehicles and drivers.')}
        />
        <PageBody>
          <LoadingMessage label={t('dispatch.messages.loading', 'Loading dispatch board…')} />
        </PageBody>
      </Page>
    )
  }

  if (legsQuery.isError) {
    return (
      <Page>
        <PageHeader
          title={t('dispatch.pages.board.title', 'Dispatch board')}
          description={t('dispatch.pages.board.description', 'Manage transport legs and assign vehicles and drivers.')}
        />
        <PageBody>
          <ErrorMessage
            label={t('dispatch.messages.loadErrorTitle', 'Could not load dispatch board')}
            description={t('dispatch.messages.loadError', 'Try again in a moment or refresh the page.')}
          />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title={t('dispatch.pages.board.title', 'Dispatch board')}
        description={t('dispatch.pages.board.description', 'Manage transport legs and assign vehicles and drivers.')}
        actions={(
          <Button type="button" onClick={() => setCreateLegOpen(true)}>
            {t('dispatch.actions.addLeg', 'Add leg')}
          </Button>
        )}
      />
      <PageBody>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="legs">{t('dispatch.table.tabs.legs', 'Legs')}</TabsTrigger>
            <TabsTrigger value="dispatches">{t('dispatch.table.tabs.dispatches', 'Dispatches')}</TabsTrigger>
          </TabsList>

          <TabsContent value="legs">
            <DynamicTable
              title={t('dispatch.pages.legs.tableTitle', 'Legs')}
              columns={legsColumns}
              data={legsQuery.data?.items ?? []}
              sortable={true}
              sorting={legsSorting}
              onSortingChange={(next) => { setLegsSorting(next); setLegsPage(1) }}
              pagination={legsQuery.data ? {
                page: legsQuery.data.page,
                pageSize: legsQuery.data.pageSize,
                total: legsQuery.data.total,
                totalPages: legsQuery.data.totalPages,
                onPageChange: setLegsPage,
              } : undefined}
              searchValue={legsSearch}
              onSearchChange={(value) => { setLegsSearch(value); setLegsPage(1) }}
              searchPlaceholder={t('dispatch.pages.board.searchPlaceholder', 'Search by cargo ID or notes')}
              filters={legsFilters}
              filterValues={legsFilterValues}
              onFiltersApply={(next) => { setLegsFilterValues(next); setLegsPage(1) }}
              onFiltersClear={() => { setLegsFilterValues({}); setLegsPage(1) }}
              rowActions={(row) => (
                <RowActions
                  items={[
                    ...(row.status === 'CREATED' ? [{
                      id: 'assign',
                      label: t('dispatch.actions.assign', 'Assign'),
                      onSelect: () => {
                        setAssignTarget(row)
                        setAssignDialogOpen(true)
                      },
                    }] : []),
                    {
                      id: 'edit',
                      label: t('dispatch.actions.edit', 'Edit'),
                      onSelect: () => {
                        setEditLegTarget(row)
                        setEditLegOpen(true)
                      },
                    },
                    {
                      id: 'delete',
                      label: t('dispatch.actions.delete', 'Delete'),
                      onSelect: () => { void handleDeleteLeg(row) },
                      destructive: true,
                    },
                  ]}
                />
              )}
            />
          </TabsContent>

          <TabsContent value="dispatches">
            {dispatchesQuery.isLoading && !dispatchesQuery.data ? (
              <LoadingMessage label={t('dispatch.messages.loading', 'Loading dispatch board…')} />
            ) : dispatchesQuery.isError ? (
              <ErrorMessage
                label={t('dispatch.messages.loadErrorTitle', 'Could not load dispatch board')}
                description={t('dispatch.messages.loadError', 'Try again in a moment or refresh the page.')}
              />
            ) : (
              <DynamicTable
                title={t('dispatch.pages.dispatches.tableTitle', 'Dispatches')}
                columns={dispatchesColumns}
                data={dispatchesQuery.data?.items ?? []}
                sortable={true}
                sorting={dispatchesSorting}
                onSortingChange={(next) => { setDispatchesSorting(next); setDispatchesPage(1) }}
                pagination={dispatchesQuery.data ? {
                  page: dispatchesQuery.data.page,
                  pageSize: dispatchesQuery.data.pageSize,
                  total: dispatchesQuery.data.total,
                  totalPages: dispatchesQuery.data.totalPages,
                  onPageChange: setDispatchesPage,
                } : undefined}
                filters={dispatchesFilters}
                filterValues={dispatchesFilterValues}
                onFiltersApply={(next) => { setDispatchesFilterValues(next); setDispatchesPage(1) }}
                onFiltersClear={() => { setDispatchesFilterValues({}); setDispatchesPage(1) }}
                onRowClick={handleDispatchRowClick}
                rowActions={(row) => (
                  <RowActions
                    items={[
                      {
                        id: 'open',
                        label: t('dispatch.actions.viewDetail', 'View detail'),
                        href: `/backend/dispatch/${row.id}`,
                      },
                      {
                        id: 'edit',
                        label: t('dispatch.actions.edit', 'Edit'),
                        onSelect: () => {
                          setEditDispatchTarget(row)
                          setEditDispatchOpen(true)
                        },
                      },
                      {
                        id: 'unassign',
                        label: t('dispatch.actions.unassign', 'Unassign'),
                        onSelect: () => { void handleUnassignDispatch(row) },
                        destructive: true,
                      },
                    ]}
                  />
                )}
              />
            )}
          </TabsContent>
        </Tabs>
      </PageBody>

      {ConfirmDialogElement}

      <LegFormDialog
        open={createLegOpen}
        onOpenChange={setCreateLegOpen}
        onSuccess={() => { void refreshAll() }}
      />

      <LegFormDialog
        open={editLegOpen}
        onOpenChange={(open) => {
          if (!open) setEditLegTarget(null)
          setEditLegOpen(open)
        }}
        leg={editLegTarget}
        onSuccess={() => { void refreshAll() }}
      />

      <AssignmentDialog
        open={assignDialogOpen}
        onOpenChange={(open) => {
          if (!open) setAssignTarget(null)
          setAssignDialogOpen(open)
        }}
        leg={assignTarget}
        onSuccess={() => { void refreshAll() }}
      />

      <DispatchEditDialog
        open={editDispatchOpen}
        onOpenChange={(open) => {
          if (!open) setEditDispatchTarget(null)
          setEditDispatchOpen(open)
        }}
        dispatch={editDispatchTarget}
        onSuccess={() => { void refreshAll() }}
      />
    </Page>
  )
}
